import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { map, Observable } from 'rxjs';
import { API_ENDPOINTS } from '../config/api-endpoints';
import { Payment, Page } from '../models';

export interface StatPayment {
  totalPayments: number;
  paymentsInPeriod: number;
  totalRevenue: number;
  totalRefunds: number;
  averagePaymentAmount: number;
  statusCounts: { [key: string]: number };
}

export interface PaymentIntentRequest {
  orderId: number;
  invoiceNumber: string;
  amount: number;
  paymentMethod: string;
  customerName?: string;
  customerEmail?: string;
  barId: string;
}

export interface PaymentIntentResponse {
  redirectUrl: string;
  transactionId: string;
}

@Injectable({
  providedIn: 'root'
})
export class PaymentService {

  constructor(private http: HttpClient) { }

  getStatistics(startDate: string, endDate: string): Observable<StatPayment> {
    return this.http.get<StatPayment>(`payment:${API_ENDPOINTS.PAYMENTS.STATISTICS(startDate, endDate)}`);
  }

  getPayments(page: number = 0, size: number = 20, status?: string,
              sort?: string): Observable<Payment[]> {
    let params: any = { page, size };
    if (status) params.status = status;
    // Without a sort the first page is the OLDEST payments, so a caller asking
    // for "the recent ones" silently got the first ever recorded once the
    // table outgrew its page size.
    if (sort) params.sort = sort;
    return this.http.get<any>(`payment:${API_ENDPOINTS.PAYMENTS.LIST}`, { params })
      .pipe(map(response => response.content));
  }

  getPaymentsByStatus(status: string): Observable<Payment[]> {
    return this.http.get<Payment[]>(`payment:${API_ENDPOINTS.PAYMENTS.BY_STATUS(status)}`);
  }

  getPaymentById(id: string | number): Observable<Payment> {
    return this.http.get<Payment>(`payment:${API_ENDPOINTS.PAYMENTS.DETAILS(id)}`);
  }

  processPayment(paymentData: Partial<Payment>): Observable<Payment> {
    return this.http.post<Payment>(`payment:${API_ENDPOINTS.PAYMENTS.PROCESS}`, paymentData);
  }

  updatePayment(id: string | number, paymentData: Partial<Payment>): Observable<Payment> {
    return this.http.put<Payment>(`payment:${API_ENDPOINTS.PAYMENTS.DETAILS(id)}`, paymentData);
  }

  updatePaymentStatus(id: string | number, status: string): Observable<Payment> {
    return this.http.put<Payment>(`payment:${API_ENDPOINTS.PAYMENTS.STATUS(id, status)}`, { status });
  }

  refundPayment(id: string | number, refundAmount: number, reason: string): Observable<Payment> {
    return this.http.post<Payment>(`payment:${API_ENDPOINTS.PAYMENTS.REFUND(id)}`, null, {
      params: { refundAmount: refundAmount.toString(), reason }
    });
  }

  /**
   * Confirm cash was physically received. Until this is called the payment is
   * PENDING — the customer choosing "pay at counter" records an intention, not
   * a settlement.
   */
  confirmCashReceived(id: string | number): Observable<Payment> {
    return this.http.post<Payment>(`payment:${API_ENDPOINTS.PAYMENTS.CONFIRM_CASH(id)}`, null);
  }

  createPaymentIntent(data: PaymentIntentRequest): Observable<PaymentIntentResponse> {
    return this.http.post<PaymentIntentResponse>(`payment:${API_ENDPOINTS.PAYMENTS.INTENT}`, data);
  }

  pollPaymentStatus(transactionId: string): Observable<Payment> {
    return this.http.get<Payment>(`payment:${API_ENDPOINTS.PAYMENTS.TRANSACTION(transactionId)}`);
  }

  getPaymentsByOrder(orderId: number | string): Observable<Payment[]> {
    return this.http.get<Payment[]>(`payment:${API_ENDPOINTS.PAYMENTS.ORDER_PAYMENTS(orderId)}`);
  }

  getPaymentsByInvoice(invoiceNumber: string): Observable<Payment[]> {
    return this.http.get<Payment[]>(`payment:${API_ENDPOINTS.PAYMENTS.BY_INVOICE(invoiceNumber)}`);
  }

  getPaymentsByMethod(method: string): Observable<Payment[]> {
    return this.http.get<Payment[]>(`payment:${API_ENDPOINTS.PAYMENTS.BY_METHOD(method)}`);
  }

  getPaymentsByDateRange(startDate: string, endDate: string): Observable<Payment[]> {
    return this.http.get<Payment[]>(`payment:${API_ENDPOINTS.PAYMENTS.DATE_RANGE}`, {
      params: { startDate, endDate }
    });
  }

  getFailedPayments(page: number = 0, size: number = 20): Observable<Payment[]> {
    return this.http.get<Payment[]>(`payment:${API_ENDPOINTS.PAYMENTS.FAILED}`, {
      params: { page: page.toString(), size: size.toString() }
    });
  }

  getRefundedPayments(page: number = 0, size: number = 20): Observable<Payment[]> {
    return this.http.get<Payment[]>(`payment:${API_ENDPOINTS.PAYMENTS.REFUNDED}`, {
      params: { page: page.toString(), size: size.toString() }
    });
  }

  searchPayments(filters: {
    transactionId?: string;
    orderId?: number;
    invoiceNumber?: string;
    paymentMethod?: string;
    status?: string;
    startDate?: string;
    endDate?: string;
    page?: number;
    size?: number;
  }): Observable<Page<Payment>> {
    let params = new HttpParams();
    if (filters.transactionId) params = params.set('transactionId', filters.transactionId);
    if (filters.orderId != null) params = params.set('orderId', filters.orderId.toString());
    if (filters.invoiceNumber) params = params.set('invoiceNumber', filters.invoiceNumber);
    if (filters.paymentMethod) params = params.set('paymentMethod', filters.paymentMethod);
    if (filters.status) params = params.set('status', filters.status);
    params = params.set('page', (filters.page ?? 0).toString());
    params = params.set('size', (filters.size ?? 20).toString());
    return this.http.get<Page<Payment>>(`payment:${API_ENDPOINTS.PAYMENTS.SEARCH}`, { params });
  }

  getTopMethodsByCount(page: number = 0, size: number = 5): Observable<any[]> {
    return this.http.get<any[]>(`payment:${API_ENDPOINTS.PAYMENTS.TOP_METHODS_COUNT}`, {
      params: { page: page.toString(), size: size.toString() }
    });
  }

  getTopMethodsByAmount(page: number = 0, size: number = 5): Observable<any[]> {
    return this.http.get<any[]>(`payment:${API_ENDPOINTS.PAYMENTS.TOP_METHODS_AMOUNT}`, {
      params: { page: page.toString(), size: size.toString() }
    });
  }
}
